// Client-side mirror of backend/services/rule_validator.py + rule_icons.py.
// Keep in sync with ALLOWED_* frozensets on the backend — scripts/validate-tag-rules.mjs
// runs the same checks at prebuild time.

export type RuleType = 'scan' | 'format' | 'search'
export type Tier = 'free' | 'pro' | 'enterprise'
export type Privacy = 'local' | 'cloud'
// A2F-803: `document` is a singular virtual target — one element per
// scan, conditions evaluate once. Mirrors src/pro/types.ts::Target and
// backend ALLOWED_TARGETS.
export type Target = 'paragraphs' | 'tables' | 'cells' | 'images' | 'contentControls' | 'document'
export type Severity = 'info' | 'warn' | 'fail'
export type Operator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'greaterThan'
  | 'lessThan'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'matches'

export type IconKey =
  | 'search'
  | 'eye'
  | 'paintbrush'
  | 'shield'
  | 'list'
  | 'lock'
  | 'flag'
  | 'wand'
  | 'layers'
  | 'link'
  | 'type'
  | 'grid'
  | 'alert'
  | 'check'
  | 'file'
  | 'bookmark'
  | 'zap'
  | 'layout'
  | 'pen'
  | 'code'

export interface ScanCondition {
  property: string
  operator: Operator
  value?: string | number | boolean
}

export interface ScanRule {
  id: string
  type: 'scan'
  tier: Tier
  privacy: Privacy
  label: string
  icon: IconKey
  description?: string
  targets: Target[]
  conditions: ScanCondition[]
  conditionLogic: 'any' | 'all'
  action: 'flag'
  severity: Severity
  message: string
}

export interface FormatRule {
  id: string
  type: 'format'
  tier: Tier
  privacy: Privacy
  label: string
  icon: IconKey
  description?: string
  targets: Target[]
  match?: { style?: string[] }
  apply: Record<string, Record<string, string | number | boolean>>
}

export interface SearchEntry {
  pattern: string
  useRegex?: boolean
  caseSensitive?: boolean
  invertMatch?: boolean
  severity: Severity
  message: string
  suggestion?: string
}

// A2F-1013: discriminator for the deterministic cover-letter contact extractor.
// Kept in lockstep with src/pro/types.ts::ContactRole.
export type ContactRole = 'regulatory' | 'technical'

export interface SearchRule {
  id: string
  type: 'search'
  tier: Tier
  privacy: Privacy
  label: string
  icon: IconKey
  description?: string
  searches: SearchEntry[]
  // A2F-1013: when set, routes execution to the cover-letter contact extractor
  // filtered to this role. Absent → coarse regex search (unchanged behaviour).
  contactRole?: ContactRole
}

export type Rule = ScanRule | FormatRule | SearchRule

// ── Packs (A2F-537) ──────────────────────────────────────────────
//
// A pack is a curated bundle of rules a user enables with one click
// (think ESLint `extends: "recommended"`). The client-side `PACKS`
// registry (src/web/data/packs.ts) owns pack definitions as code; the
// profile JSONB blob only stores pack IDs (see `Profile.packs` below).
// This keeps pack content in a reviewable code path — new FDA/EMA/TGA
// packs ship as frontend commits, not as per-user data migrations.
export interface Pack {
  id: string
  name: string
  description: string
  tier: Tier
  version: string
  // References into ALL_RULES — the prebuild validator checks every
  // entry resolves. Empty array is valid (placeholder packs that ship
  // before their content — see fda_module_3_v0).
  ruleIds: string[]
  // A2F-796: editorial override for pack sort. Default sort is tier
  // first (free above pro), then usage frequency within tier; a numeric
  // displayOrder bypasses the within-tier ordering when set.
  displayOrder?: number
}

export interface Profile {
  domain: string
  tags?: string[]
  rules: Rule[]
  // Pack IDs selected by the user. The pack's ruleIds contribute into
  // the merged `rules` array at configurator save time, so the add-in
  // always executes from `rules` — `packs` is bookkeeping for the
  // configurator's own hydration.
  packs?: string[]
  // A2F-796: packs the user has explicitly toggled OFF from the My
  // Chrona pack-grouped surface. A pack renders as active iff its id
  // is in `packs` AND NOT in `disabledPacks`. Kept separate from
  // `packs` so toggling OFF in the taskpane is non-destructive — the
  // pack's rules stay in `rules`, just hidden, so re-enabling is
  // instant and doesn't need the rule defs on the Pro bundle side.
  disabledPacks?: string[]
  // A2F-650: house-style overrides keyed by formatter group. Mirrors
  // chrona-addin/src/pro/types.ts::Formatters and the JSONB shape the
  // backend's formatter_validator already accepts. Optional and only
  // populated when the user explicitly customizes formatting on the FRE
  // redesign — D70's inherit-don't-copy default still holds for every
  // other caller of buildProfile.
  formatters?: Formatters
}

// ── Formatters (A2F-650) ─────────────────────────────────────────
//
// Mirrors chrona-addin/src/pro/types.ts. The pro/web split is a bundler
// boundary (webpack vs. Vite), so the shape is duplicated rather than
// imported. Keep these in lockstep.

export interface BodyLikeFormatter {
  font?: string
  size?: number
  bold?: boolean
  italic?: boolean
  color?: string
  lineSpacing?: number
  spaceBefore?: number
  spaceAfter?: number
  alignment?: 'Left' | 'Right' | 'Center' | 'Justified'
}

export type HeadingLevel = '1' | '2' | '3' | '4' | '5' | '6'
export type HeadingLevelMap<T> = Partial<Record<HeadingLevel, T>>

export interface HeadingsFormatter {
  font?: string
  // A2F-730: see pro/types.ts::HeadingsFormatter for the per-level
  // resolution rule. `fonts[level] || font` at apply time.
  fonts?: HeadingLevelMap<string>
  sizes?: HeadingLevelMap<number>
  bold?: HeadingLevelMap<boolean>
  italic?: HeadingLevelMap<boolean>
  colors?: HeadingLevelMap<string>
  spaceBefore?: HeadingLevelMap<number>
  spaceAfter?: HeadingLevelMap<number>
  pageBreakBefore?: HeadingLevelMap<boolean>
  keepWithNext?: HeadingLevelMap<boolean>
}

export interface TablesFormatter {
  headerBg?: string
  headerFont?: string
  headerFontSize?: number
  headerTextColor?: string
  bodyFont?: string
  bodyFontSize?: number
  bodyTextColor?: string
  defaultCellBg?: string
  altRowBg?: string
  borderColor?: string
  borderWidth?: number
}

// A2F-701: one user-defined Apply Table Style preset. Each carries an
// id (slug, validated server-side against [a-z0-9_-]{1,64}), a name,
// and a 12-field palette mirroring TablesFormatter + headerBold. The
// taskpane reads tableStyles from the cached profile and renders a
// click-to-apply row per entry. Keep in lockstep with
// backend/services/formatter_validator.py::_TABLE_STYLE_PALETTE_SCHEMA
// and TABLE_STYLE_DEFAULTS in src/commands/formatting.js.
export interface TableStylePreset {
  id: string
  name: string
  headerBg?: string
  headerFont?: string
  headerFontSize?: number
  headerTextColor?: string
  headerBold?: boolean
  bodyFont?: string
  bodyFontSize?: number
  bodyTextColor?: string
  defaultCellBg?: string
  altRowBg?: string
  borderColor?: string
  borderWidth?: number
}

export interface Formatters {
  body?: BodyLikeFormatter
  lists?: BodyLikeFormatter
  figureCaption?: BodyLikeFormatter
  // A2F-700-caption: tableCaption joins figureCaption now that A2F-691's
  // ribbon handler reads getActiveStyle("tableCaption") from the user's
  // profile. Missing key falls through to CHRONA_DEFAULT.tableCaption.
  tableCaption?: BodyLikeFormatter
  headings?: HeadingsFormatter
  tables?: TablesFormatter
  tableStyles?: TableStylePreset[]
}

export interface Subscription {
  tier: 'free' | 'trial' | 'pro'
  trialStartedAt: string | null
  trialEndsAt: string | null
  isTrialExpired: boolean
  // A2F-767 Stripe billing — populated by /api/v1/webhooks/stripe.
  // null on free/trial users who've never completed checkout.
  status?: 'active' | 'past_due' | 'canceled' | 'incomplete' | string | null
  stripeCustomerId?: string | null
  currentPeriodEnd?: string | null
  // When set, the subscription will cancel on this date and won't
  // auto-renew. Mutually exclusive with the "Renews on" UI state.
  // Stripe sends this on Customer Portal cancel; reactivation clears
  // it via another subscription.updated event.
  cancelAt?: string | null
  // First-time Pro conversion timestamp. Doesn't change across cycles.
  convertedAt?: string | null
}

export interface UnsupportedResult {
  unsupported: true
  reason: string
  suggestedLabel: string
  suggestedIcon: IconKey
  confidence: number
}

export type TranslateResult = Rule | UnsupportedResult

export function isUnsupported(r: TranslateResult): r is UnsupportedResult {
  return (r as UnsupportedResult).unsupported === true
}

// ── Constants that mirror rule_validator.py caps ────────────────
//
// A2F-537 raised the cap from 15 → 30. A2F-796 raised it again to 100
// once pack grouping replaced the flat list as the navigation surface:
// the per-pack collapse + custom-rules bucket means rule count is no
// longer the constraint on findability.
export const MAX_RULES_PER_PROFILE = 100
export const MAX_LABEL_LEN = 40
export const MAX_DESCRIPTION_LEN = 300

// Sanity cap on pack IDs in a single profile. The real guardrail is
// MAX_RULES_PER_PROFILE — packs' rules count individually against it.
// 5 is belt-and-suspenders; plausible usage is 1–2 packs matching the
// user's regulatory context.
export const MAX_PACKS_PER_PROFILE = 5
// A2F-797: `disabledPacks` capped separately — see the corresponding
// comment in backend/services/rule_validator.py. Mirrored here to keep
// the client + server constants in lockstep, even though no client
// path currently enforces the length (the configurator dedupes
// disabledPacks against packs, so growth above the cap requires
// stale buildup from pack churn).
export const MAX_DISABLED_PACKS = 10

// Pack IDs: lowercase-start, alnum + underscore, ≤ 50 chars. Kept in
// lockstep with backend/services/rule_validator.py::PACK_ID_RE.
export const PACK_ID_RE = /^[a-z][a-z0-9_]{0,49}$/

export type TagKey =
  | 'fda_submissions'
  | 'tga_australia'
  | 'clinical_protocols'
  | 'lab_reports'
  | 'legal_briefs'
  | 'policy_documents'
  | 'technical_reports'
  | 'sops'
  | 'ectd_compliant'
  | 'company_branding'
  | 'table_formatting'
  | 'consistent_headings'
  | 'auto_captions'
  | 'cover_letter_validation'
  | 'terminology_checks'
  | 'multi_jurisdiction'

export type TagSection = 'what_you_write' | 'what_you_care_about'

export interface TagLabel {
  key: TagKey
  label: string
  section: TagSection
}
